// ═══════════════════════════════════════════════════════════════
// ImpactPortalAssess — Assess tab
// Views: home | vida | quick
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useRef } from 'react';
import { IconMicrophone, IconList, IconCamera, IconMapPin, IconX, IconSparkles } from '@tabler/icons-react';
import { useAuthStore } from '../../../store/authStore';
import { useImpactStore } from '../../../store/impactStore';
import { useDebrisOpsStore } from '../../../store/debrisOpsStore';
import { reverseGeocode } from '../lib/gis';
import { AddressAutocomplete } from '../../../components/ui/AddressAutocomplete';
import { WORKER_URL } from '../../../lib/api-switch';
import VIDA from '../VIDA';
import '../../shelter/portal/ShelterPortal.css';
import './ImpactPortal.css';

// ── Helpers ───────────────────────────────────────────────────

function resizeImageToBase64(file: File, maxDim = 1024): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        const scale = maxDim / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      canvas.getContext('2d')!.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', 0.8).split(',')[1]);
      URL.revokeObjectURL(img.src);
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

// ── Constants ─────────────────────────────────────────────────

const IA_DAMAGE_CATEGORIES = [
  { key: 'affected',     label: 'Affected',     color: '#16a34a' },
  { key: 'minor',        label: 'Minor',        color: '#d97706' },
  { key: 'major',        label: 'Major',        color: '#ea580c' },
  { key: 'destroyed',    label: 'Destroyed',    color: '#B12525' },
  { key: 'inaccessible', label: 'Inaccessible', color: '#7c3aed' },
];

const PA_CATEGORIES = [
  { value: 'A',  label: 'A - Buildings' },
  { value: 'B',  label: 'B - Equipment' },
  { value: 'C',  label: 'C - Utilities' },
  { value: 'D',  label: 'D - Roads' },
  { value: 'E',  label: 'E - Water Control' },
  { value: 'F',  label: 'F - Public Buildings' },
  { value: 'G',  label: 'G - Parks/Recreation' },
  { value: 'Z',  label: 'Z - Other' },
];

type AssessView = 'home' | 'vida' | 'quick';

// ── Quick Capture Form ────────────────────────────────────────

interface QuickFormProps {
  onSuccess: () => void;
}

function QuickForm({ onSuccess }: QuickFormProps) {
  const { activeIncident, user } = useAuthStore();
  const { createAssessment, uploadPhoto } = useImpactStore();
  const { createRequest } = useDebrisOpsStore();

  // GPS
  const [gpsStatus, setGpsStatus] = useState<'acquiring' | 'locked' | 'error'>('acquiring');
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [address, setAddress] = useState('');
  const geocodedRef = useRef(false);

  // Form state
  const [assessType, setAssessType] = useState<'IA' | 'PA'>('IA');
  const [iaCategory, setIaCategory] = useState('affected');
  const [facilityName, setFacilityName] = useState('');
  const [paCategory, setPaCategory] = useState('A');
  const [description, setDescription] = useState('');

  // Photos
  const [photoThumbs, setPhotoThumbs] = useState<string[]>([]);
  const [photoFiles, setPhotoFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Debris tagging
  const [tagForDebris, setTagForDebris] = useState(false);

  // AI
  const [aiLoading, setAiLoading] = useState(false);

  // Submit
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [savedId, setSavedId] = useState<number | null>(null);

  // GPS watch
  useEffect(() => {
    if (!navigator.geolocation) { setGpsStatus('error'); return; }
    const watchId = navigator.geolocation.watchPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setCoords({ lat, lng });
        setGpsStatus('locked');
        if (!geocodedRef.current) {
          geocodedRef.current = true;
          try {
            const result = await reverseGeocode(lat, lng);
            if (result?.address) setAddress(result.address);
          } catch { /* ignore */ }
        }
      },
      () => setGpsStatus('error'),
      { enableHighAccuracy: true, timeout: 15000 },
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, []);

  const handlePhotoAdd = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []).slice(0, 10 - photoFiles.length);
    const thumbs = await Promise.all(
      files.map(f => new Promise<string>(resolve => {
        const reader = new FileReader();
        reader.onload = ev => resolve(ev.target?.result as string);
        reader.readAsDataURL(f);
      }))
    );
    setPhotoFiles(prev => [...prev, ...files]);
    setPhotoThumbs(prev => [...prev, ...thumbs]);
    e.target.value = '';
  };

  const removePhoto = (i: number) => {
    setPhotoFiles(prev => prev.filter((_, idx) => idx !== i));
    setPhotoThumbs(prev => prev.filter((_, idx) => idx !== i));
  };

  const handleAiDescribe = async () => {
    if (photoFiles.length === 0) return;
    setAiLoading(true);
    try {
      const images = await Promise.all(
        photoFiles.slice(0, 5).map(async (file: File) => ({
          type: 'image' as const,
          source: {
            type: 'base64' as const,
            media_type: 'image/jpeg' as const,
            data: await resizeImageToBase64(file, 1024),
          },
        }))
      );
      const photoCount = images.length;
      const res = await fetch(`${WORKER_URL}/api/claude`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Origin': 'https://apps.opengov.com' },
        body: JSON.stringify({
          model: 'claude-sonnet-4-6',
          max_tokens: 800,
          messages: [{
            role: 'user',
            content: [
              ...images,
              {
                type: 'text',
                text: `I have uploaded ${photoCount} photo${photoCount > 1 ? 's' : ''} from a ${assessType === 'IA' ? 'residential' : 'infrastructure'} damage assessment. Describe the damage visible across ALL ${photoCount} photo${photoCount > 1 ? 's' : ''}, noting what each one shows if they capture different angles or areas. Be concise but thorough (${photoCount > 1 ? '3-5' : '2-3'} sentences). Focus on: type of damage, severity, and structural impact. Do not use emojis.`,
              },
            ],
          }],
        }),
      });
      const result = await res.json();
      if (result.content?.[0]?.text) {
        setDescription(result.content[0].text);
      }
    } catch (err) {
      console.error('AI describe error:', err);
    } finally {
      setAiLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!activeIncident) return;
    setError('');
    setSubmitting(true);
    try {
      const id = await createAssessment({
        incidentId:     String(activeIncident.id),
        assessmentType: assessType === 'IA' ? 'individual' : 'public',
        damageCategory: assessType === 'IA' ? iaCategory : '',
        paCategory:     assessType === 'PA' ? paCategory : '',
        severity:       null,
        address:        address || '',
        county:         '',
        fipsCode:       '',
        stateCode:      '',
        latitude:       coords?.lat ?? null,
        longitude:      coords?.lng ?? null,
        damageTypes:    [],
        description:    description || '',
        method:         'quick',
        assessorName:   user?.fullName || '',
        assessorEmail:  user?.email || '',
        facilityName:   assessType === 'PA' ? facilityName : '',
        residenceType:  '',
        ownership:      '',
        dwellingType:   '',
      });
      if (id && photoFiles.length > 0) {
        for (let i = 0; i < photoFiles.length; i++) {
          try {
            const b64 = await resizeImageToBase64(photoFiles[i]);
            await uploadPhoto(id, b64, i);
          } catch { /* non-blocking */ }
        }
      }
      if (id && tagForDebris) {
        try {
          await createRequest({
            incidentId:     String(activeIncident.id),
            assessmentId:   String(id),
            address:        address || '',
            latitude:       coords?.lat ? String(coords.lat) : '',
            longitude:      coords?.lng ? String(coords.lng) : '',
            damageCategory: assessType === 'IA' ? iaCategory : '',
            damageTypes:    '[]',
            description:    description || '',
            status:         'pending',
            taggedBy:       user?.email ?? '',
            taggedAt:       new Date().toISOString(),
          });
        } catch { /* non-blocking */ }
      }
      setSavedId(id);
      setSuccess(true);
    } catch {
      setError('Failed to save. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setAddress('');
    setAssessType('IA');
    setIaCategory('affected');
    setFacilityName('');
    setPaCategory('A');
    setDescription('');
    setPhotoFiles([]);
    setPhotoThumbs([]);
    setTagForDebris(false);
    setSuccess(false);
    setSavedId(null);
    setError('');
    geocodedRef.current = false;
  };

  if (success) {
    return (
      <div className="ip-success">
        <div className="ip-success-icon">✅</div>
        <div className="ip-success-title">Assessment Saved!</div>
        {savedId && <div className="ip-success-sub">Record #{savedId}</div>}
        <div className="ip-success-btns">
          <button className="ip-vida-btn" style={{ height: 52, fontSize: 16 }} onClick={resetForm}>
            Submit Another
          </button>
          <button className="ip-quick-btn" onClick={onSuccess}>
            View Reports
          </button>
        </div>
      </div>
    );
  }

  const selectedIaCat = IA_DAMAGE_CATEGORIES.find(c => c.key === iaCategory);

  return (
    <div className="ip-quick-wrap">
      {/* GPS bar */}
      <div className={`vida-gps-bar ${gpsStatus}`} style={{ margin: '16px 0' }}>
        <IconMapPin size={14} />
        {gpsStatus === 'acquiring' && <span>Acquiring GPS…</span>}
        {gpsStatus === 'locked'    && <span>{address || `${coords?.lat?.toFixed(5)}, ${coords?.lng?.toFixed(5)}`}</span>}
        {gpsStatus === 'error'     && <span>GPS unavailable — enter address below</span>}
      </div>

      {/* Address */}
      <div className="sp-field">
        <label className="ip-section-label">Address</label>
        <AddressAutocomplete
          value={address}
          onChange={(val, lat, lng) => {
            setAddress(val);
            if (lat !== undefined && lng !== undefined) {
              setCoords({ lat, lng });
              geocodedRef.current = true;
            }
          }}
          placeholder="Address or location description"
        />
      </div>

      {/* IA / PA toggle */}
      <div className="sp-field">
        <label className="ip-section-label">Assessment Type</label>
        <div className="ip-type-toggle">
          <button className={`ip-type-btn${assessType === 'IA' ? ' selected' : ''}`} onClick={() => setAssessType('IA')}>
            IA — Individual
          </button>
          <button className={`ip-type-btn${assessType === 'PA' ? ' selected' : ''}`} onClick={() => setAssessType('PA')}>
            PA — Public
          </button>
        </div>
      </div>

      {/* IA: Damage category */}
      {assessType === 'IA' && (
        <div className="sp-field">
          <label className="ip-section-label">Damage Category</label>
          <div className="ip-damage-grid">
            {IA_DAMAGE_CATEGORIES.map(cat => (
              <button
                key={cat.key}
                className={`ip-damage-btn${iaCategory === cat.key ? ' selected' : ''}`}
                style={{ '--dot-color': cat.color } as React.CSSProperties}
                onClick={() => setIaCategory(cat.key)}
              >
                <div className="ip-damage-dot" style={{ background: cat.color }} />
                {cat.label}
              </button>
            ))}
          </div>
          {selectedIaCat && (
            <div style={{ marginTop: 8, fontSize: 12, color: selectedIaCat.color, fontWeight: 600 }}>
              Selected: {selectedIaCat.label}
            </div>
          )}
        </div>
      )}

      {/* PA: Facility + category */}
      {assessType === 'PA' && (
        <>
          <div className="sp-field">
            <label className="ip-section-label">Facility Name</label>
            <input
              className="sp-input"
              value={facilityName}
              onChange={e => setFacilityName(e.target.value)}
              placeholder="e.g., City Hall, Route 9 Bridge"
            />
          </div>
          <div className="sp-field">
            <label className="ip-section-label">PA Category</label>
            <select
              className="sp-input"
              value={paCategory}
              onChange={e => setPaCategory(e.target.value)}
              style={{ appearance: 'auto' }}
            >
              {PA_CATEGORIES.map(c => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
        </>
      )}

      {/* Photos */}
      <div className="sp-field">
        <label className="ip-section-label">Photos (up to 10)</label>
        {photoThumbs.length > 0 && (
          <div className="ip-photo-thumbs">
            {photoThumbs.map((thumb, i) => (
              <div key={i} className="ip-photo-thumb">
                <img src={thumb} alt={`Photo ${i + 1}`} />
                <button className="ip-photo-remove" onClick={() => removePhoto(i)}>
                  <IconX size={10} />
                </button>
              </div>
            ))}
          </div>
        )}
        {photoFiles.length < 10 && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: 'none' }}
              onChange={handlePhotoAdd}
            />
            <div className="ip-photo-tap" onClick={() => fileInputRef.current?.click()}>
              <IconCamera size={22} />
              <span>{photoFiles.length === 0 ? 'Tap to add photo' : 'Add another photo'}</span>
            </div>
          </>
        )}
        {photoFiles.length > 0 && (
          <button
            className="ip-ai-describe-btn"
            onClick={handleAiDescribe}
            disabled={aiLoading}
            type="button"
          >
            <IconSparkles size={16} />
            {aiLoading ? 'Generating…' : 'AI Describe'}
          </button>
        )}
      </div>

      {/* Description */}
      <div className="sp-field">
        <label className="ip-section-label">Description (optional)</label>
        <textarea
          className="sp-input"
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="Brief description of damage observed…"
          rows={3}
          style={{ resize: 'vertical' }}
        />
      </div>

      {/* Debris removal toggle */}
      <button
        className={`ip-debris-toggle${tagForDebris ? ' active' : ''}`}
        onClick={() => setTagForDebris(v => !v)}
        type="button"
      >
        <span className="ip-debris-toggle-dot" />
        <span>Tag for Debris Removal</span>
        <span className="ip-debris-toggle-state">{tagForDebris ? 'ON' : 'OFF'}</span>
      </button>

      {error && <div className="sp-error" style={{ marginBottom: 16 }}>{error}</div>}

      <button className="ip-submit-btn" onClick={handleSubmit} disabled={submitting}>
        {submitting ? 'Saving…' : tagForDebris ? '✓ Submit & Tag for Debris' : '✓ Submit Assessment'}
      </button>
    </div>
  );
}

// ── Main Assess Tab ───────────────────────────────────────────

interface ImpactPortalAssessProps {
  onViewReports: () => void;
}

export default function ImpactPortalAssess({ onViewReports }: ImpactPortalAssessProps) {
  const { activeIncident } = useAuthStore();
  const [assessView, setAssessView] = useState<AssessView>('home');

  if (assessView === 'vida') {
    return (
      <div style={{ padding: '16px 16px 100px' }}>
        <button className="ip-back-btn" onClick={() => setAssessView('home')}>
          ← Back to Assess
        </button>
        <VIDA />
      </div>
    );
  }

  if (assessView === 'quick') {
    return (
      <div>
        <div style={{ padding: '16px 16px 0' }}>
          <button className="ip-back-btn" onClick={() => setAssessView('home')}>
            ← Back to Assess
          </button>
        </div>
        <QuickForm onSuccess={onViewReports} />
      </div>
    );
  }

  // home view
  return (
    <div className="ip-assess-home">
      <div>
        <div className="ip-section-label">Damage Assessment</div>
        <div style={{ fontSize: 14, color: '#7B7D7F', marginTop: 2 }}>
          {activeIncident?.name || ''}
        </div>
      </div>

      <button className="ip-vida-btn" onClick={() => setAssessView('vida')}>
        <IconMicrophone size={32} />
        Start Voice Assessment
      </button>

      <button className="ip-quick-btn" onClick={() => setAssessView('quick')}>
        <IconList size={18} />
        Quick Capture Form
      </button>
    </div>
  );
}
